import { Block } from "../engine/blocks";
import { clamp, n01, smoothstep, type SimplexWorld } from "../engine/noise";

export const Biome = {
  OCEAN: 0,
  BEACH: 1,
  PLAINS: 2,
  FOREST: 3,
  BIRCH: 4,
  TAIGA: 5,
  DESERT: 6,
  SAVANNA: 7,
  JUNGLE: 8,
  SWAMP: 9,
  CHERRY: 10,
  SNOW: 11,
  MOUNTAIN: 12,
  PEAK: 13,
} as const;

export type BiomeId = (typeof Biome)[keyof typeof Biome];

export interface Climate {
  temp: number;
  humid: number;
  weird: number;
  cont: number;
  erosion: number;
}

export interface BiomeDef {
  id: BiomeId;
  name: string;
  grass: [number, number, number];
  leaves: [number, number, number];
  water: [number, number, number];
  fog: [number, number, number];
  surface: number;
  fill: number;
  tree: "none" | "oak" | "birch" | "pine" | "cherry" | "jungle" | "cactus" | "acacia";
  treeChance: number;
  flowerChance: number;
  grassChance: number;
}

export const BIOMES: Record<number, BiomeDef> = {
  [Biome.OCEAN]: {
    id: Biome.OCEAN,
    name: "Ocean",
    grass: [62, 140, 110],
    leaves: [52, 120, 88],
    water: [42, 128, 168],
    fog: [148, 188, 210],
    surface: Block.SAND,
    fill: Block.SAND,
    tree: "none",
    treeChance: 0,
    flowerChance: 0,
    grassChance: 0,
  },
  [Biome.BEACH]: {
    id: Biome.BEACH,
    name: "Beach",
    grass: [186, 196, 92],
    leaves: [92, 156, 72],
    water: [64, 168, 186],
    fog: [210, 210, 186],
    surface: Block.SAND,
    fill: Block.SAND,
    tree: "none",
    treeChance: 0,
    flowerChance: 0.01,
    grassChance: 0.02,
  },
  [Biome.PLAINS]: {
    id: Biome.PLAINS,
    name: "Meadow",
    grass: [124, 186, 72],
    leaves: [86, 160, 64],
    water: [56, 150, 168],
    fog: [186, 210, 168],
    surface: Block.GRASS,
    fill: Block.DIRT,
    tree: "oak",
    treeChance: 0.008,
    flowerChance: 0.08,
    grassChance: 0.28,
  },
  [Biome.FOREST]: {
    id: Biome.FOREST,
    name: "Woods",
    grass: [78, 148, 62],
    leaves: [62, 132, 52],
    water: [48, 128, 140],
    fog: [164, 188, 148],
    surface: Block.GRASS,
    fill: Block.DIRT,
    tree: "oak",
    treeChance: 0.085,
    flowerChance: 0.04,
    grassChance: 0.18,
  },
  [Biome.BIRCH]: {
    id: Biome.BIRCH,
    name: "Birch Grove",
    grass: [142, 188, 86],
    leaves: [168, 204, 92],
    water: [72, 164, 168],
    fog: [198, 214, 176],
    surface: Block.GRASS,
    fill: Block.DIRT,
    tree: "birch",
    treeChance: 0.07,
    flowerChance: 0.06,
    grassChance: 0.2,
  },
  [Biome.TAIGA]: {
    id: Biome.TAIGA,
    name: "Taiga",
    grass: [72, 132, 92],
    leaves: [36, 96, 72],
    water: [52, 120, 140],
    fog: [168, 186, 186],
    surface: Block.GRASS,
    fill: Block.DIRT,
    tree: "pine",
    treeChance: 0.09,
    flowerChance: 0.015,
    grassChance: 0.1,
  },
  [Biome.DESERT]: {
    id: Biome.DESERT,
    name: "Desert",
    grass: [196, 180, 86],
    leaves: [92, 140, 56],
    water: [64, 168, 176],
    fog: [226, 210, 164],
    surface: Block.SAND,
    fill: Block.SAND,
    tree: "cactus",
    treeChance: 0.02,
    flowerChance: 0.004,
    grassChance: 0.01,
  },
  [Biome.SAVANNA]: {
    id: Biome.SAVANNA,
    name: "Savanna",
    grass: [176, 164, 62],
    leaves: [124, 148, 48],
    water: [56, 148, 148],
    fog: [214, 196, 140],
    surface: Block.GRASS,
    fill: Block.DIRT,
    tree: "acacia",
    treeChance: 0.018,
    flowerChance: 0.03,
    grassChance: 0.16,
  },
  [Biome.JUNGLE]: {
    id: Biome.JUNGLE,
    name: "Jungle",
    grass: [36, 148, 58],
    leaves: [18, 124, 42],
    water: [28, 120, 118],
    fog: [118, 168, 128],
    surface: Block.GRASS,
    fill: Block.DIRT,
    tree: "jungle",
    treeChance: 0.13,
    flowerChance: 0.07,
    grassChance: 0.35,
  },
  [Biome.SWAMP]: {
    id: Biome.SWAMP,
    name: "Marsh",
    grass: [92, 124, 48],
    leaves: [72, 108, 40],
    water: [48, 96, 78],
    fog: [148, 164, 128],
    surface: Block.GRASS,
    fill: Block.DIRT,
    tree: "oak",
    treeChance: 0.03,
    flowerChance: 0.05,
    grassChance: 0.22,
  },
  [Biome.CHERRY]: {
    id: Biome.CHERRY,
    name: "Blossom Vale",
    grass: [156, 196, 92],
    leaves: [232, 132, 168],
    water: [92, 176, 188],
    fog: [226, 196, 210],
    surface: Block.GRASS,
    fill: Block.DIRT,
    tree: "cherry",
    treeChance: 0.055,
    flowerChance: 0.12,
    grassChance: 0.22,
  },
  [Biome.SNOW]: {
    id: Biome.SNOW,
    name: "Frostfield",
    grass: [186, 206, 196],
    leaves: [148, 176, 168],
    water: [140, 186, 204],
    fog: [198, 214, 222],
    surface: Block.SNOWY_GRASS,
    fill: Block.DIRT,
    tree: "pine",
    treeChance: 0.025,
    flowerChance: 0,
    grassChance: 0.02,
  },
  [Biome.MOUNTAIN]: {
    id: Biome.MOUNTAIN,
    name: "Highlands",
    grass: [108, 148, 86],
    leaves: [72, 124, 72],
    water: [72, 140, 164],
    fog: [176, 188, 196],
    surface: Block.GRASS,
    fill: Block.STONE,
    tree: "pine",
    treeChance: 0.02,
    flowerChance: 0.02,
    grassChance: 0.08,
  },
  [Biome.PEAK]: {
    id: Biome.PEAK,
    name: "Peaks",
    grass: [210, 218, 222],
    leaves: [164, 180, 176],
    water: [148, 188, 210],
    fog: [186, 198, 210],
    surface: Block.SNOW,
    fill: Block.STONE,
    tree: "none",
    treeChance: 0,
    flowerChance: 0,
    grassChance: 0,
  },
};

export function biomeDef(id: number): BiomeDef {
  return BIOMES[id] ?? BIOMES[Biome.PLAINS]!;
}

export function sampleClimate(noise: SimplexWorld, wx: number, wz: number): Climate {
  const warpX = noise.fbm2(wx * 0.0009 + 12.2, wz * 0.0009 - 4.8, 4) * 48;
  const warpZ = noise.fbm2(wx * 0.0009 - 7.1, wz * 0.0009 + 19.4, 4) * 48;
  const x = wx + warpX;
  const z = wz + warpZ;

  const temp = n01(noise.fbm2(x * 0.00105 + 40, z * 0.00105, 5));
  const humid = n01(noise.fbm2(x * 0.0012 - 90, z * 0.0012 + 20, 5));
  const weird = n01(noise.fbm2(x * 0.0024 + 210, z * 0.0024 - 80, 4));
  const cont = n01(noise.fbm2(x * 0.00062 + 8, z * 0.00062, 6));
  const erosion = n01(noise.fbm2(x * 0.0009 - 30, z * 0.0009 + 55, 5));
  return { temp, humid, weird, cont, erosion };
}

export function pickBiome(c: Climate, height: number, sea: number): BiomeId {
  if (c.cont < 0.42 && height < sea + 1) return Biome.OCEAN;
  if (height <= sea + 2 && c.cont < 0.56) return Biome.BEACH;
  if (height > 92) return Biome.PEAK;
  if (height > 78) return Biome.MOUNTAIN;

  if (c.temp < 0.28) return Biome.SNOW;
  if (c.temp < 0.4) return Biome.TAIGA;

  if (c.temp > 0.78 && c.humid < 0.32) return Biome.DESERT;
  if (c.temp > 0.7 && c.humid < 0.5) return Biome.SAVANNA;
  if (c.temp > 0.68 && c.humid > 0.62) return Biome.JUNGLE;

  if (c.humid > 0.72 && c.temp > 0.4 && c.temp < 0.7) return Biome.SWAMP;
  if (c.weird > 0.72 && c.temp > 0.42 && c.temp < 0.72 && c.humid > 0.4) {
    return Biome.CHERRY;
  }
  if (c.humid < 0.38) return Biome.PLAINS;
  if (c.weird > 0.55 && c.temp > 0.45) return Biome.BIRCH;
  if (c.humid > 0.45) return Biome.FOREST;
  return Biome.PLAINS;
}

export interface ColumnSample {
  height: number;
  biome: BiomeId;
  climate: Climate;
}

export function sampleHeight(noise: SimplexWorld, wx: number, wz: number, sea: number): number {
  const c = sampleClimate(noise, wx, wz);
  const land = smoothstep(0.36, 0.54, c.cont);

  const continent = 22 + c.cont * 30;
  const hills = n01(noise.fbm2(wx * 0.007 + 3, wz * 0.007, 4)) * 16;
  const detail = noise.fbm2(wx * 0.03, wz * 0.03, 3) * 3.2;

  const mountainMask =
    smoothstep(0.58, 0.82, c.cont) * smoothstep(0.7, 0.35, c.erosion) * land;
  const ridge = noise.ridged2(wx * 0.0028 + 9, wz * 0.0028, 5);
  const mountains = Math.pow(ridge, 1.45) * 46 * mountainMask;

  const oceanFloor = 18 + c.cont * 18 + noise.fbm2(wx * 0.01, wz * 0.01, 3) * 4;
  let h = lerp(oceanFloor, continent + hills + detail + mountains, land);

  // river gouges on land — shallow enough that sea-level fill reads as water, not a canyon
  const river = Math.abs(noise.fbm2(wx * 0.0036 + 70, wz * 0.0036 - 15, 4));
  const riverMask = land * smoothstep(0.028, 0.0, river) * smoothstep(68, 52, h);
  h -= riverMask * 5;

  // swamp sink
  if (c.humid > 0.7 && c.temp > 0.4 && c.temp < 0.7 && land > 0.7) {
    h = lerp(h, sea - 0.4, 0.45);
  }

  return clamp(Math.floor(h), 2, 126);
}

export function sampleColumn(noise: SimplexWorld, wx: number, wz: number, sea: number): ColumnSample {
  const climate = sampleClimate(noise, wx, wz);
  const height = sampleHeight(noise, wx, wz, sea);
  const biome = pickBiome(climate, height, sea);
  return { height, biome, climate };
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
